import { Editor, Element, Node, Path, Text, Transforms } from 'slate';

export const cleanupHandlers = {
  // [PURPOSE]: Removes adjacent logical operators from a paragraph.
  cleanupAdjacentLogicalOperators(editor: Editor, node: Node, path: Path) {
    // This rule operates on paragraphs, not individual operators
    if (!(Element.isElement(node) && node.type === 'paragraph')) {
      return false;
    }

    const children = [...Node.children(editor, path)];
    let prevLogicalOperatorPath: Path | null = null;
    let normalizationHappened = false;
    const pathsToRemove: Path[] = [];

    for (const [child, childPath] of children) {
      // Skip empty/whitespace text nodes
      if (Text.isText(child) && child.text?.trim() === '') {
        continue;
      }

      if (Element.isElement(child) && child.type === 'logical-operator') {
        // If the previous non-empty node was also a logical operator, mark the current one for removal
        if (prevLogicalOperatorPath) {
          pathsToRemove.push(childPath);
        }
        // Update the path of the last seen logical operator
        prevLogicalOperatorPath = childPath;
      } else {
        // Reset if the node is not a logical operator
        prevLogicalOperatorPath = null;
      }
    }

    // If we found adjacent operators to remove
    if (pathsToRemove.length > 0) {
      Editor.withoutNormalizing(editor, () => {
        // Remove nodes in reverse order to avoid path conflicts within this pass
        for (let i = pathsToRemove.length - 1; i >= 0; i--) {
          // Check if the node still exists before removing (important due to potential previous removals)
          if (Editor.hasPath(editor, pathsToRemove[i])) {
            Transforms.removeNodes(editor, { at: pathsToRemove[i] });
          }
        }
      });
      normalizationHappened = true;
    }

    return normalizationHappened;
  },

  // [PURPOSE]: Removes grouping operators without meaningful content, logical operators, and/or empty/whitespace text nodes.
  cleanupFunctionallyEmptyGroups(editor: Editor, node: Node, path: Path) {
    if (!(Element.isElement(node) && node.type === 'paragraph')) {
      return false;
    }

    let hasMeaningfulContent = false;
    let hasClosingParen = false;

    for (const [child] of Node.children(editor, path)) {
      if (Text.isText(child) && child.text?.trim() === '') {
        // Ignore empty text nodes
        continue;
      }
      if (Element.isElement(child)) {
        if (child.type === 'logical-operator') {
          // Ignore logical operators
          continue;
        }
        if (child.type === 'grouping-operator') {
          if (child.operator === ')') {
            hasClosingParen = true; // Found the closing paren
          }
          // Ignore grouping operators (both opening and closing) for content check
          continue;
        }
      }

      // Found something else (chip, simple-chip, non-empty text, etc.)
      hasMeaningfulContent = true;
      // Optimization: If we found meaningful content and a closing paren, we can stop early
      if (hasClosingParen) {
        break;
      }
    }

    // If no meaningful content was found AND it has a closing parenthesis, remove the group
    if (!hasMeaningfulContent && hasClosingParen) {
      Editor.withoutNormalizing(editor, () => {
        // Remove the empty group node
        Transforms.removeNodes(editor, { at: path });
      });

      return true; // Normalization occurred
    }

    return false; // Group has content, or lacks a closing paren, no cleanup needed
  },

  // [PURPOSE]: Cleans up paragraphs that contain only logical operators and whitespace by removing the logical operators.
  cleanupLogicalOperatorsInEmptyParagraph(editor: Editor, node: Node, path: Path) {
    if (!(Element.isElement(node) && node.type === 'paragraph')) {
      return false;
    }

    let onlyAllowedNodesFound = true;
    const logicalOperatorPaths: Path[] = [];

    for (const [child, childPath] of Node.children(editor, path)) {
      if (Text.isText(child) && child.text?.trim() === '') {
        // Allowed: space text node
        continue;
      } else if (Element.isElement(child) && child.type === 'logical-operator') {
        // Allowed: logical operator, collect its path
        logicalOperatorPaths.push(childPath);
        continue;
      } else {
        // Found a disallowed node type
        onlyAllowedNodesFound = false;
        break;
      }
    }

    // If only allowed nodes were found and there are logical operators to remove
    if (onlyAllowedNodesFound && logicalOperatorPaths.length > 0) {
      Editor.withoutNormalizing(editor, () => {
        // Remove nodes in reverse order to avoid path conflicts
        for (let i = logicalOperatorPaths.length - 1; i >= 0; i--) {
          Transforms.removeNodes(editor, { at: logicalOperatorPaths[i] });
        }
      });

      return true; // Normalization occurred
    }

    return false; // No cleanup needed
  },

  // [PURPOSE]: Cleans up paragraphs that contain only logical operators and whitespace by removing the logical operators.
  cleanupLogicalOperatorsLeadingParagraph(editor: Editor, node: Node, path: Path) {
    if (!(Element.isElement(node) && node.type === 'paragraph')) {
      return false;
    }

    // Get all child nodes of this paragraph
    const children = [...Node.children(editor, path)];

    // Keep track of logical operators to remove
    const logicalOperatorPaths: Path[] = [];

    // Flag to determine if we've found a non-whitespace, non-logical-operator node
    let foundNonEmptyContent = false;

    // Examine each child in order
    for (const [child, childPath] of children) {
      if (Text.isText(child) && child.text?.trim() === '') {
        // Skip empty text nodes (whitespace) but keep them
        continue;
      } else if (Element.isElement(child) && child.type === 'logical-operator') {
        // Check if we've already found non-empty content
        if (foundNonEmptyContent) {
          // We've already found meaningful content, so this logical operator should stay
          break;
        }
        // This is a logical operator at the beginning, mark it for removal
        logicalOperatorPaths.push(childPath);
      } else {
        // Found a non-whitespace, non-logical-operator node
        foundNonEmptyContent = true;
        break;
      }
    }

    // If there are logical operators to remove at the beginning
    if (logicalOperatorPaths.length > 0) {
      Editor.withoutNormalizing(editor, () => {
        // Remove nodes in reverse order to avoid path conflicts
        for (let i = logicalOperatorPaths.length - 1; i >= 0; i--) {
          Transforms.removeNodes(editor, { at: logicalOperatorPaths[i] });
        }
      });

      return true; // Normalization occurred
    }

    return false; // No cleanup needed
  },
};

export default cleanupHandlers;
